import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { t } from 'i18next';
import { Children, isValidElement, ReactElement } from 'react';
import { TertiaryFeature } from '../../utils/types';
import { Icon } from '../Icon';
import { useNavigationContext } from '../../context/NavigationProvider';
import { TertiaryFeatureDto } from '../../utils/principal-service/dto/GetFeatures.dto';

type TertiaryNavigationItemProps = {
  id: TertiaryFeature;
  children: ReactElement;
};

export type TertiaryNavigationProps = {
  children: React.ReactNode;
  onItemSelected?: (id: string) => void;
  isDisabled?: boolean;
};

export const TertiaryNavigationItem = ({
  children,
}: TertiaryNavigationItemProps) => children;

export const TertiaryNavigation = ({
  children,
  onItemSelected = () => null,
  isDisabled,
}: TertiaryNavigationProps) => {
  const { tertiaryFeature, setTertiaryFeature } = useNavigationContext();
  const items: TertiaryNavigationItemProps[] = Children.toArray(children)
    .filter((child): child is ReactElement => isValidElement(child))
    .map((child) => {
      return {
        id: child.props.id,
        children: child.props.children,
      };
    });

  const handleChange = (value: TertiaryFeatureDto) => {
    setTertiaryFeature(value);
    onItemSelected(value.id);
  };

  return (
    <div className="grid w-full grid-cols-12">
      <Listbox
        value={tertiaryFeature}
        onChange={handleChange}
        disabled={items.length <= 1 || isDisabled}
      >
        {({ open, disabled }) => (
          <div className="min-w-72 relative col-span-full md:col-span-4 lg:col-span-3">
            <ListboxButton
              className={`text-grey-700 w-full border-b bg-white px-3 py-2 font-sans text-base/4 font-semibold outline-none ${
                disabled
                  ? 'border-grey-200'
                  : 'border-grey-500 hover:border-blue-100 hover:text-blue-100'
              }`}
            >
              <div
                className={`flex w-full items-center justify-between ${
                  open ? 'text-blue-100' : ''
                }`}
              >
                <span>
                  {t(`features:tertiary-features:${tertiaryFeature?.id}`)}
                </span>
                <span className="h-4 w-4">
                  <Icon
                    name={open ? 'ArrowUp' : 'ArrowDown'}
                    className={disabled ? 'text-grey-200' : ''}
                  />
                </span>
              </div>
            </ListboxButton>
            <ListboxOptions className="absolute z-10 max-h-60 w-full overflow-auto bg-white shadow-lg ring-1 ring-blue-100 focus:outline-none">
              {items?.map((item) => (
                <ListboxOption
                  key={item.id}
                  value={item.id}
                  className={({ focus }) =>
                    `cursor-pointer select-none px-3 py-2 ${
                      focus ? 'hover:text-blue-100' : ''
                    }`
                  }
                >
                  {({ selected }) => (
                    <div className="flex w-full items-center justify-between">
                      <span className="w-full">
                        {t(`features:tertiary-features:${item.id}`)}
                      </span>
                      {selected ? (
                        <span className="h-4 w-4">
                          <Icon name="Checkmark" />
                        </span>
                      ) : null}
                    </div>
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        )}
      </Listbox>

      <div className="relative col-span-full w-full pt-7">
        {items.find((item) => item.id === tertiaryFeature?.id)?.children}
      </div>
    </div>
  );
};

TertiaryNavigation.Item = TertiaryNavigationItem;
